import { useEffect, useState } from "react";
import Modal from "../../layouts/Modal";
import { IVehicle } from "../../models/Vehicle";
import { ArrowTopRightOnSquareIcon, CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";
import PriceFormat from "../../utils/priceFormat";
import { s } from "@fullcalendar/core/internal-common";


export interface IBulkResponse {
  data: string,
  message: string,
  success: boolean
}

export interface IBulkVehicleAction {
  title: string;
  boldDescription?: string;
  action: (vehicles: IVehicle[]) => Promise<IBulkResponse[]>;
  buttonText: string;
}

interface IVehicleConfirmationModalProps {
  vehicles: IVehicle[];
  bulkAction: IBulkVehicleAction | null;
  open: boolean;
  setOpen: any;
}

// #region Response Body
function ResponseBody({
  response, 
  setResponse, 
  actionedVehicles, 
  actionText
} : {
  response        : IBulkResponse[], 
  setResponse     : (response: IBulkResponse[] | null) => void,
  actionedVehicles: IVehicle[],
  actionText      : string
}) {
  return (
    <div className="mt-4">
      <p>
        Tried to <ActionText actionText={actionText} /> {response.length} vehicle{response.length > 1 && "s"}.</p>
      <br />

      <div className="flex flex-col gap-y-2 divide-y">
        {response.map((res: IBulkResponse, index: number) => {
          const foundVehicle = actionedVehicles.find(vehicle => vehicle.id === res.data);

          return (
            <div className="flex justify-between pt-2" key={index}>
              <div className="flex grow items-center gap-x-2">
                {res.success ? <CheckCircleIcon className="text-green-500 w-7 h-7" /> : <XCircleIcon className="text-red-500 w-7 h-7" />}
                <p className="whitespace-pre-line">{res.message}</p>
              </div>
              <Link
                to={`/vehicles/${res.data}`} target="_blank" rel="noopener noreferrer"
                className="underline text-blue-500 hover:text-blue-700"
              >
                {foundVehicle?.leads[0]?.contact?.fullname} - {foundVehicle?.vrm} <ArrowTopRightOnSquareIcon className="inline-block mb-1 w-4 h-4" />
              </Link>
            </div>
          )
        })}
      </div>
      <div className="flex justify-end">
        <ButtonInput label="Reset" isSubmit={false} onClick={() => setResponse(null)} classes={"mt-6"} />
      </div>
    </div>
  );
}
// #endregion

// #region VehicleModalBase
function VehicleModalBase({
  title,
  children,
  open,
  setOpen
}: {
  title: string;
  children: React.ReactNode;
  open: boolean;
  setOpen: any;
}) {
  return (
    <Modal width="max-w-4xl" open={open} setOpen={setOpen} >
      <h1 className="text-xl">{title}</h1>
      <hr />
      {children}
    </Modal >
  )
}
// #endregion

function ActionText({actionText}: {actionText: string}) {
  return (
    <span className="italic text-primary-600 font-semibold">
      {actionText}
    </span> 
  );
}

export default function VehicleConfirmationModal({ 
  vehicles, 
  bulkAction,
  open, 
  setOpen 
} : IVehicleConfirmationModalProps) {
  const [response, setResponse] = useState<IBulkResponse[] | null>(null);
  const [vehiclesSelected, setVehiclesSelected] = useState<IVehicle[]>([]);
  const [actionedVehicles, setActionedVehicles] = useState<IVehicle[]>([]);
  const [loading, setLoading] = useState(false);
  // --------------------------- //
  useEffect(() => {
    if (!open) {
      setResponse(null);
      setVehiclesSelected([]);
      setActionedVehicles([]);
      setLoading(false);
    }
  }, [open]);
  // --------------------------- //
  const handleSelected = (vehicleId: string) => {
    if (vehiclesSelected.find(v => v.id === vehicleId)) {
      setVehiclesSelected(vehiclesSelected.filter((vehicle: IVehicle) => vehicle.id !== vehicleId));
    } else {
      const vehicleToAdd = vehicles.find(v => v.id === vehicleId);
      if (vehicleToAdd) {
        setVehiclesSelected([...vehiclesSelected, vehicleToAdd]);
      }
    }
  }
  if (!bulkAction) return null;
  // --------------------------- //
  if (response) return (
    <VehicleModalBase title={bulkAction.title} open={open} setOpen={setOpen}>
      <ResponseBody
        response={response}
        setResponse={setResponse}
        actionedVehicles={actionedVehicles}
        actionText={bulkAction.buttonText}
      />
    </VehicleModalBase>
  )
  // --------------------------- //
  if (loading) return (
    <VehicleModalBase title={bulkAction.title} open={open} setOpen={setOpen}>
      <div className="mt-4">
        <LoadingWheel />
      </div>
    </VehicleModalBase>
  )
  // --------------------------- //
  return (
    <VehicleModalBase title={bulkAction.title} open={open} setOpen={setOpen}>
      <div className="mt-4">
        <p>Are you sure you want to <ActionText actionText={bulkAction.buttonText} /> all of these vehicles?</p>
        {bulkAction.boldDescription && (
          <p className="font-semibold">
            {bulkAction.boldDescription}
          </p>
        )}
        <br />
        <p>For confirmation, please select all the vehicles you want to <ActionText actionText={bulkAction.buttonText} />.</p>
        <p className="font-semibold">{vehiclesSelected.length} vehicle{(vehiclesSelected.length > 1 || vehiclesSelected.length === 0) && "s"} selected.</p>
        <br />

        {/* Table of passed in vehicles */}
        <div className="space-y-2 divide-y">
          {vehicles.map((vehicle: IVehicle) =>
              <div key={vehicle.id} className="grid grid-cols-6 items-center">
                <img
                  className="w-24 h-12 object-contain"
                  src={`${vehicle != null ? vehicle.vehicleImageUrl : null}`}
                />
                <span>{vehicle.leads[0]?.contact?.fullname}</span>
                <span>{vehicle.vrm}</span>
                <span>{vehicle.postcode}</span>
                <span>{PriceFormat(vehicle.leads[0]?.price)}</span>
                <input type="checkbox" onChange={() => handleSelected(vehicle.id)} />
              </div>
            )
          }
        </div>

        <ButtonInput
          disabled={vehiclesSelected.length < 1}
          label={bulkAction.buttonText}
          isSubmit={false}
          onClick={() => {
            setLoading(true);
            bulkAction.action(vehiclesSelected).then((res: IBulkResponse[]) => {
              setLoading(false);
              const actedVehicles = vehicles.filter(vehicle => vehiclesSelected.find(v => v.id === vehicle.id));
              setVehiclesSelected([]);
              
              if (res.length === 0) return;
              setActionedVehicles(actedVehicles);
              setResponse(res);
            })
          }}
          classes={"mt-6"}
        />
      </div>
    </VehicleModalBase >
  )
}